import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABELS } from "../lib/constants";
import { toast } from "sonner";
import {
  Cog, CirclePlus, UsersRound, Trash, DoorOpen, ArrowRight, LoaderCircle, UserRoundPlus, Building2,
} from "lucide-react";

export default function AdminPanel() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState(null);
  const [users, setUsers] = useState([]);
  const [clubName, setClubName] = useState("");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "coach" });

  const load = async () => {
    const { data } = await api.get("/admin/projects");
    setProjects(data);
  };
  useEffect(() => { load(); }, []);

  const loadUsers = async (pid) => {
    const { data } = await api.get("/admin/users", { params: { project_id: pid } });
    setUsers(data);
  };

  const openProject = async (p) => {
    setSelected(p);
    await loadUsers(p.id);
  };

  const addProject = async (e) => {
    e.preventDefault();
    if (!clubName.trim()) return;
    setCreating(true);
    try {
      await api.post("/admin/projects", { club_name: clubName });
      setClubName("");
      toast.success("تم إنشاء النادي");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const deleteProject = async (id) => {
    if (!window.confirm("حذف النادي وكل بياناته؟")) return;
    await api.delete(`/admin/projects/${id}`);
    toast.success("تم الحذف");
    if (selected?.id === id) setSelected(null);
    load();
  };

  const addUser = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/users", { ...form, project_id: selected.id });
      toast.success("تم إنشاء الحساب");
      setForm({ name: "", email: "", password: "", role: "coach" });
      loadUsers(selected.id);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const deleteUser = async (id) => {
    if (!window.confirm("حذف الحساب؟")) return;
    await api.delete(`/admin/users/${id}`);
    loadUsers(selected.id);
    load();
  };

  return (
    <div dir="rtl" className="min-h-screen p-4 lg:p-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#2a3b19] flex items-center justify-center">
            <Cog className="text-white" size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-black">لوحة المدير</h1>
            <p className="text-slate-600 text-sm">إدارة الأندية والحسابات</p>
          </div>
        </div>
        <button
          data-testid="admin-logout"
          onClick={() => { logout(); navigate("/login"); }}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-red-500 hover:bg-red-500/10"
        >
          <DoorOpen size={18} /> خروج
        </button>
      </div>

      {!selected ? (
        <>
          <form onSubmit={addProject} className="glass border border-[#d1fae5] rounded-2xl p-5 mb-6 flex gap-3">
            <input
              data-testid="club-name-input"
              value={clubName}
              onChange={(e) => setClubName(e.target.value)}
              placeholder="اسم النادي الجديد"
              className="flex-1 px-4 py-3 rounded-xl bg-[#f7fdfa] border border-[#d1fae5] focus:border-[#2a3b19] outline-none"
            />
            <button
              data-testid="add-club-btn"
              disabled={creating}
              className="px-5 py-3 rounded-xl bg-[#2a3b19] hover:bg-[#3a4f22] text-white font-bold flex items-center gap-2"
            >
              {creating ? <LoaderCircle className="animate-spin" size={18} /> : <CirclePlus size={18} />} إنشاء نادي
            </button>
          </form>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((p) => (
              <div
                key={p.id}
                data-testid={`club-card-${p.id}`}
                className="glass border border-[#d1fae5] rounded-2xl p-5 hover:border-[#2a3b19]/50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="w-12 h-12 rounded-xl bg-[#2a3b19]/10 flex items-center justify-center">
                    <Building2 className="text-[#2a3b19]" size={26} />
                  </div>
                  <button onClick={() => deleteProject(p.id)} className="text-red-500 hover:text-red-300" data-testid={`delete-club-${p.id}`}>
                    <Trash size={18} />
                  </button>
                </div>
                <h3 className="font-bold text-lg mt-3">{p.club_name}</h3>
                <div className="flex gap-4 text-xs text-slate-600 mt-2">
                  <span><UsersRound size={12} className="inline ml-1" />{p.user_count} حساب</span>
                  <span>{p.player_count} لاعب</span>
                </div>
                <button
                  data-testid={`manage-club-${p.id}`}
                  onClick={() => openProject(p)}
                  className="w-full mt-4 py-2 rounded-xl bg-[#ecfdf5] hover:bg-[#d1fae5] text-sm font-medium flex items-center justify-center gap-2"
                >
                  <UserRoundPlus size={16} /> إدارة الحسابات
                </button>
              </div>
            ))}
            {projects.length === 0 && (
              <p className="text-slate-500 col-span-full text-center py-12">لا توجد أندية بعد. أنشئ ناديك الأول.</p>
            )}
          </div>
        </>
      ) : (
        <div>
          <button onClick={() => setSelected(null)} className="flex items-center gap-2 text-slate-600 hover:text-[#2a3b19] mb-4">
            <ArrowRight size={18} /> رجوع للأندية
          </button>
          <h2 className="text-xl font-bold mb-4">حسابات نادي: <span className="text-[#2a3b19]">{selected.club_name}</span></h2>

          <form onSubmit={addUser} className="glass border border-[#d1fae5] rounded-2xl p-5 mb-6 grid md:grid-cols-5 gap-3">
            <input data-testid="user-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="الاسم" className="px-3 py-2.5 rounded-xl bg-[#f7fdfa] border border-[#d1fae5] focus:border-[#2a3b19] outline-none" />
            <input data-testid="user-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="البريد الإلكتروني" className="px-3 py-2.5 rounded-xl bg-[#f7fdfa] border border-[#d1fae5] focus:border-[#2a3b19] outline-none" />
            <input data-testid="user-password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="الرمز" className="px-3 py-2.5 rounded-xl bg-[#f7fdfa] border border-[#d1fae5] focus:border-[#2a3b19] outline-none" />
            <select data-testid="user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="px-3 py-2.5 rounded-xl bg-[#f7fdfa] border border-[#d1fae5] focus:border-[#2a3b19] outline-none">
              <option value="coach">مدرب</option>
              <option value="assistant_coach">مساعد مدرب</option>
              <option value="goalkeeper_coach">مدرب حراس</option>
            </select>
            <button data-testid="add-user-btn" className="px-4 py-2.5 rounded-xl bg-[#2a3b19] hover:bg-[#3a4f22] text-white font-bold flex items-center justify-center gap-2">
              <CirclePlus size={16} /> إضافة
            </button>
          </form>

          <div className="space-y-2">
            {users.map((u) => (
              <div key={u.id} data-testid={`user-row-${u.id}`} className="glass border border-[#d1fae5] rounded-xl p-4 flex items-center justify-between">
                <div>
                  <div className="font-semibold">{u.name}</div>
                  <div className="text-xs text-slate-600">{u.email} · <span className="text-[#2a3b19]">{ROLE_LABELS[u.role]}</span></div>
                </div>
                <button onClick={() => deleteUser(u.id)} className="text-red-500 hover:text-red-300" data-testid={`delete-user-${u.id}`}>
                  <Trash size={18} />
                </button>
              </div>
            ))}
            {users.length === 0 && <p className="text-slate-500 text-center py-8">لا توجد حسابات. أضف حساب المدرب.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
